import React from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, Download, History } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { downloadClipVideo } from "@/lib/downloads/clip-download";
import type { ClipWithVersion, VersionGroup } from "@/lib/clips/groupClipVersions";
import { useDownloadUpsell } from "@/contexts/downloadUpsellContext";

export type VersionBadgeClip = ClipWithVersion & {
  name?: string | null;
  rendering_status?: string | null;
  finished_video_url?: string | null;
  compressed_video_url?: string | null;
  clipped_video_url?: string | null;
};

export interface ClipVersionBadgeProps {
  versionGroup?: VersionGroup<VersionBadgeClip>;
  currentClipId: string;
  currentVersionNumber?: number | null;
}

/**
 * "v2"-style pill next to a clip card title. Opens a popover listing every
 * version of the clip (newest first) with a per-version download action.
 * Renders nothing when the clip has a single version.
 */
export const ClipVersionBadge: React.FC<ClipVersionBadgeProps> = ({
  versionGroup,
  currentClipId,
  currentVersionNumber,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { notifyClipDownloaded } = useDownloadUpsell();

  if (!versionGroup || versionGroup.totalVersions <= 1) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/20 hover:bg-primary/25 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <History className="w-2.5 h-2.5" />
          v{currentVersionNumber || 1}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-2"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-semibold text-muted-foreground mb-2">
          {t('verticalClipCard.versions')}
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {versionGroup.allVersions.map((version) => {
            const vNum = version.version_number || 1;
            const isCurrent = version.id === currentClipId;
            const vDownloadUrl = version.finished_video_url || version.compressed_video_url || version.clipped_video_url;
            const isRendered = version.rendering_status === 'success' && !!vDownloadUrl;
            return (
              <div
                key={version.id}
                className={`flex items-center gap-2 p-1.5 rounded text-xs ${isCurrent ? 'bg-primary/10 font-medium' : 'hover:bg-muted/60'}`}
              >
                <span className={`shrink-0 font-semibold ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
                  v{vNum}
                </span>
                <span className="truncate flex-1 text-muted-foreground">
                  {new Date(version.createdAt).toLocaleDateString()}
                </span>
                {isRendered && (
                  <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                )}
                {isRendered && (
                  <button
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await downloadClipVideo({
                          clipId: version.id,
                          fallbackName: `${version.name || 'clip'}_v${vNum}`,
                          downloadUrl: vDownloadUrl!,
                        });
                        notifyClipDownloaded();
                      } catch {
                        toast({ title: t('projectClips.toast.downloadError'), variant: "destructive" });
                      }
                    }}
                    title={t('verticalClipCard.download')}
                  >
                    <Download className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ClipVersionBadge;
